
import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getApiBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';
import { MinuteDockUser, MinuteDockEntry } from '../types';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const apiUrl = getApiBaseUrl();

interface TaskActivity {
  taskId: string;
  taskName: string;
  clientName: string;
  projectName: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  entryCount: number;
  isRecurring: boolean;
  category: string;
  avgHoursPerEntry: number;
  uniqueUsers: number;
  users: string[];
  isBillable: boolean;
}

interface WeeklyActivityReport {
  weekOf: string;
  weekEnd: string;
  timestamp: string;
  taskActivities: TaskActivity[];
}

interface ActivitySummary {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billabilityRate: number;
  totalTasks: number;
  recurringTasks: number;
  oneOffTasks: number;
  averageTaskSize: number;
}

interface CategoryBreakdown {
  category: string;
  hours: number;
  billableHours: number;
  taskCount: number;
  percentage: number;
}

const customStyles = {
  headRow: {
    style: {
      backgroundColor: '#1976d2',
      color: '#fff',
      fontWeight: 500,
    },
  },
  headCells: {
    style: {
      padding: '12px',
    },
  },
  rows: {
    style: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#f5f5f5',
      },
      '&:hover': {
        backgroundColor: '#e0f7fa',
      },
    },
  },
  cells: {
    style: {
      padding: '12px',
    },
  },
};

const WorkActivityBreakdown = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [taskActivities, setTaskActivities] = useState<TaskActivity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<TaskActivity[]>([]);
  const [users, setUsers] = useState<MinuteDockUser[]>([]);
  const [entries, setEntries] = useState<MinuteDockEntry[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState<CategoryBreakdown[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [weeklyReports, setWeeklyReports] = useState<WeeklyActivityReport[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>('current');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBillability, setSelectedBillability] = useState('');
  const [selectedTaskType, setSelectedTaskType] = useState('');
  const [minHours, setMinHours] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getToken = async () => {
    const accounts = instance.getAllAccounts();
    const token = await acquireTokenSafe(instance, accounts);
    if (!token) throw new Error('No account found, please log in');
    return token;
  };

  const fetchHistoricalReports = async () => {
    try {
      const token = await getToken();
      const authHeader = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}/api/work-activity/weekly-reports`, {
        headers: { Authorization: authHeader },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setWeeklyReports(data.sort((a: WeeklyActivityReport, b: WeeklyActivityReport) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ));
    } catch (err) {
      console.error('Error fetching historical reports:', err);
    }
  };

  const categorizeTask = (taskName: string): string => {
    const name = taskName.toLowerCase();

    // Administrative tasks
    if (name.includes('admin') || name.includes('filing') || name.includes('compliance') || 
        name.includes('annual return') || name.includes('registration') || name.includes('form')) {
      return 'Administrative';
    }

    // Accounting & Bookkeeping
    if (name.includes('bookkeeping') || name.includes('accounts') || name.includes('payroll') || 
        name.includes('vat') || name.includes('tax') || name.includes('financial')) {
      return 'Accounting & Bookkeeping';
    }

    // Company Secretarial
    if (name.includes('cosec') || name.includes('secretary') || name.includes('director') || 
        name.includes('shares') || name.includes('board')) {
      return 'Company Secretarial';
    }

    // Consultancy
    if (name.includes('consultation') || name.includes('advice') || name.includes('meeting') || 
        name.includes('review') || name.includes('strategy')) {
      return 'Consultancy';
    }

    // Client Management
    if (name.includes('client') || name.includes('onboarding') || name.includes('communication') || 
        name.includes('relationship')) {
      return 'Client Management';
    }

    return 'Other';
  };

  const isRecurringTask = (taskName: string): boolean => {
    const name = taskName.toLowerCase();
    return name.includes('monthly') || name.includes('quarterly') || name.includes('annual') || 
           name.includes('recurring') || name.includes('ongoing') || name.includes('regular');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const authHeader = `Bearer ${token}`;

      const [usersResponse, entriesResponse, tasksResponse] = await Promise.all([
        fetch(`${apiUrl}/api/minutedock/users`, {
          headers: { Authorization: authHeader },
        }),
        fetch(`${apiUrl}/api/minutedock/entries`, {
          headers: { Authorization: authHeader },
        }),
        fetch(`${apiUrl}/api/minutedock/tasks`, {
          headers: { Authorization: authHeader },
        }),
      ]);

      const [usersData, entriesData, tasksData] = await Promise.all([
        usersResponse.json(),
        entriesResponse.json(),
        tasksResponse.json(),
      ]);

      console.log('Raw API responses:', { usersData, entriesData, tasksData });

      setUsers(usersData);
      setEntries(entriesData);

      // Calculate last week (Monday to Sunday)
      const currentDate = new Date();
      const dayOfWeek = currentDate.getDay();
      const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const lastMonday = new Date(currentDate);
      lastMonday.setDate(currentDate.getDate() - daysToLastMonday - 7);
      lastMonday.setHours(0, 0, 0, 0);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 7);
      lastSunday.setHours(0, 0, 0, 0);

      console.log('Filter Range:', { lastMonday: lastMonday.toISOString(), lastSunday: lastSunday.toISOString() });

      // Process task activities
      const taskActivityMap = new Map<string, TaskActivity>();

      // Ensure all data is properly structured as arrays
      const entries = Array.isArray(entriesData) ? entriesData : (entriesData?.entries || []);
      const tasks = Array.isArray(tasksData) ? tasksData : (tasksData?.tasks || []);
      const users = Array.isArray(usersData) ? usersData : (usersData?.users || []);

      // Filter entries for last week only
      const lastWeekEntries = entries.filter((entry: MinuteDockEntry) => {
        const entryDate = new Date(entry.logged_at);
        const isValidDate = !isNaN(entryDate.getTime());
        return (
          isValidDate &&
          entryDate >= lastMonday &&
          entryDate < lastSunday
        );
      });

      console.log('Last week entries:', lastWeekEntries.length, 'out of', entries.length);

      lastWeekEntries.forEach((entry: MinuteDockEntry) => {
        // Get the first task ID from the task_ids array
        const taskId = entry.task_ids && entry.task_ids.length > 0 ? entry.task_ids[0] : null;
        const task = tasks.find((t: any) => t.id === taskId);
        const taskName = task?.name || 'Unknown Task';
        const clientName = task?.contact_name || 'Unknown Client';
        const projectName = task?.project_name || 'No Project';

        const key = `${taskId || 'unknown'}`;

        if (!taskActivityMap.has(key)) {
          taskActivityMap.set(key, {
            taskId: taskId || 'unknown',
            taskName,
            clientName,
            projectName,
            totalHours: 0,
            billableHours: 0,
            nonBillableHours: 0,
            entryCount: 0,
            isRecurring: isRecurringTask(taskName),
            category: categorizeTask(taskName),
            avgHoursPerEntry: 0,
            uniqueUsers: 0,
            users: [],
            isBillable: false,
          });
        }

        const activity = taskActivityMap.get(key)!;
        const hours = entry.duration / 3600; // Convert seconds to hours

        activity.totalHours += hours;
        activity.entryCount += 1;

        // Check if the task is billable (assuming task has billable property)
        const isBillable = task?.billable || false;
        activity.isBillable = isBillable; // Store billable status
        if (isBillable) {
          activity.billableHours += hours;
        } else {
          activity.nonBillableHours += hours;
        }

        // Track unique users
        const user = users.find((u: any) => u.id === entry.user_id);
        const userName = user ? `${user.first_name || user.name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User' : 'Unknown User';

        if (!activity.users.includes(userName)) {
          activity.users.push(userName);
        }
      });

      // Calculate averages and finalize data
      const activities = Array.from(taskActivityMap.values()).map(activity => ({
        ...activity,
        avgHoursPerEntry: activity.totalHours / activity.entryCount,
        uniqueUsers: activity.users.length,
      }));

      setTaskActivities(activities);
      setFilteredActivities(activities);

      // Calculate summary
      const totalHours = activities.reduce((sum, a) => sum + a.totalHours, 0);
      const billableHours = activities.reduce((sum, a) => sum + a.billableHours, 0);
      const nonBillableHours = activities.reduce((sum, a) => sum + a.nonBillableHours, 0);

      setSummary({
        totalHours,
        billableHours,
        nonBillableHours,
        billabilityRate: (billableHours / totalHours) * 100,
        totalTasks: activities.length,
        recurringTasks: activities.filter(a => a.isRecurring).length,
        oneOffTasks: activities.filter(a => !a.isRecurring).length,
        averageTaskSize: totalHours / activities.length,
      });

      // Calculate category breakdown
      const categoryMap = new Map<string, { hours: number; billableHours: number; taskCount: number }>();

      activities.forEach(activity => {
        if (!categoryMap.has(activity.category)) {
          categoryMap.set(activity.category, { hours: 0, billableHours: 0, taskCount: 0 });
        }

        const cat = categoryMap.get(activity.category)!;
        cat.hours += activity.totalHours;
        cat.billableHours += activity.billableHours;
        cat.taskCount += 1;
      });

      const breakdown = Array.from(categoryMap.entries()).map(([category, data]) => ({
        category,
        hours: data.hours,
        billableHours: data.billableHours,
        taskCount: data.taskCount,
        percentage: (data.hours / totalHours) * 100,
      }));

      setCategoryBreakdown(breakdown);

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchHistoricalReports();
  }, []);

  useEffect(() => {
    if (selectedWeek === 'current') {
      // Use current week data - refetch to ensure latest
      fetchData();
    } else {
      // Use historical data
      const selectedReport = weeklyReports.find(report => report.weekOf === selectedWeek);
      if (selectedReport) {
        setTaskActivities(selectedReport.taskActivities);
        setFilteredActivities(selectedReport.taskActivities);
        
        // Recalculate summary and category breakdown for historical data
        const activities = selectedReport.taskActivities;
        const totalHours = activities.reduce((sum, a) => sum + a.totalHours, 0);
        const billableHours = activities.reduce((sum, a) => sum + a.billableHours, 0);
        const nonBillableHours = activities.reduce((sum, a) => sum + a.nonBillableHours, 0);

        setSummary({
          totalHours,
          billableHours,
          nonBillableHours,
          billabilityRate: totalHours > 0 ? (billableHours / totalHours) * 100 : 0,
          totalTasks: activities.length,
          recurringTasks: activities.filter(a => a.isRecurring).length,
          oneOffTasks: activities.filter(a => !a.isRecurring).length,
          averageTaskSize: totalHours > 0 ? totalHours / activities.length : 0,
        });

        // Calculate category breakdown
        const categoryMap = new Map<string, { hours: number; billableHours: number; taskCount: number }>();
        activities.forEach(activity => {
          if (!categoryMap.has(activity.category)) {
            categoryMap.set(activity.category, { hours: 0, billableHours: 0, taskCount: 0 });
          }
          const cat = categoryMap.get(activity.category)!;
          cat.hours += activity.totalHours;
          cat.billableHours += activity.billableHours;
          cat.taskCount += 1;
        });

        const breakdown = Array.from(categoryMap.entries()).map(([category, data]) => ({
          category,
          hours: data.hours,
          billableHours: data.billableHours,
          taskCount: data.taskCount,
          percentage: totalHours > 0 ? (data.hours / totalHours) * 100 : 0,
        }));

        setCategoryBreakdown(breakdown);
      }
    }
  }, [selectedWeek, weeklyReports]);

  useEffect(() => {
    let filtered = taskActivities;

    if (searchTerm) {
      filtered = filtered.filter(activity =>
        activity.taskName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.projectName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(activity => activity.category === selectedCategory);
    }

    if (selectedBillability === 'billable') {
      filtered = filtered.filter(activity => activity.billableHours > 0);
    } else if (selectedBillability === 'non-billable') {
      filtered = filtered.filter(activity => activity.nonBillableHours > 0);
    }

    if (selectedTaskType === 'recurring') {
      filtered = filtered.filter(activity => activity.isRecurring);
    } else if (selectedTaskType === 'one-off') {
      filtered = filtered.filter(activity => !activity.isRecurring);
    }

    if (minHours) {
      const minHoursNum = parseFloat(minHours);
      filtered = filtered.filter(activity => activity.totalHours >= minHoursNum);
    }

    setFilteredActivities(filtered);
  }, [taskActivities, searchTerm, selectedCategory, selectedBillability, selectedTaskType, minHours]);

  const exportToCSV = () => {
    const headers = [
      'Task Name',
      'Project',
      'Category',
      'Total Hours',
      'Billable Hours',
      'Non-Billable Hours',
      'Is Billable',
      'Entry Count',
      'Avg Hours/Entry',
      'Unique Users',
      'Task Type',
    ];

    const csvData = filteredActivities.map(activity => [
      activity.taskName,
      activity.projectName,
      activity.category,
      activity.totalHours.toFixed(2),
      activity.billableHours.toFixed(2),
      activity.nonBillableHours.toFixed(2),
      activity.isBillable ? 'Yes' : 'No',
      activity.entryCount,
      activity.avgHoursPerEntry.toFixed(2),
      activity.uniqueUsers,
      activity.isRecurring ? 'Recurring' : 'One-off',
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'work_activity_breakdown.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: TableColumn<TaskActivity>[] = [
    {
      name: 'Task Name',
      selector: (row: TaskActivity) => row.taskName,
      sortable: true,
      wrap: true,
      width: '300px',
    },
    {
      name: 'Category',
      selector: (row: TaskActivity) => row.category,
      sortable: true,
      cell: (row: TaskActivity) => (
        <Chip
          label={row.category}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      ),
      width: '160px',
    },
    {
      name: 'Total Hours',
      selector: (row: TaskActivity) => row.totalHours,
      sortable: true,
      format: (row: TaskActivity) => row.totalHours.toFixed(1),
      width: '120px',
    },
    {
      name: 'Billable Hours',
      selector: (row: TaskActivity) => row.billableHours,
      sortable: true,
      format: (row: TaskActivity) => row.billableHours.toFixed(1),
      width: '140px',
    },
    {
      name: 'Is Billable',
      selector: (row: TaskActivity) => row.isBillable,
      sortable: true,
      cell: (row: TaskActivity) => (
        <Chip
          label={row.isBillable ? 'Yes' : 'No'}
          size="small"
          sx={{
            backgroundColor: row.isBillable ? '#e8f5e8' : '#f5f5f5',
            color: row.isBillable ? '#2e7d32' : '#757575',
            fontSize: '0.75rem',
            minWidth: '60px',
            fontWeight: 'medium',
          }}
        />
      ),
      width: '120px',
    },
    {
      name: 'Entries',
      selector: (row: TaskActivity) => row.entryCount,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Unique Users',
      selector: (row: TaskActivity) => row.uniqueUsers,
      sortable: true,
      width: '100px',
    },
    {
      name: 'Task Type',
      selector: (row: TaskActivity) => row.isRecurring ? 'Recurring' : 'One-off',
      sortable: true,
      cell: (row: TaskActivity) => (
        <Chip
          label={row.isRecurring ? 'Recurring' : 'One-off'}
          size="small"
          color={row.isRecurring ? 'primary' : 'default'}
          sx={{ fontSize: '0.75rem' }}
        />
      ),
      width: '100px',
    },
  ];

  // Chart data
  const categoryChartData = {
    labels: categoryBreakdown.map(cat => cat.category),
    datasets: [
      {
        data: categoryBreakdown.map(cat => cat.hours),
        backgroundColor: [
          '#1976d2',
          '#388e3c',
          '#f57c00',
          '#d32f2f',
          '#7b1fa2',
          '#0288d1',
        ],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const billabilityChartData = {
    labels: categoryBreakdown.map(cat => cat.category),
    datasets: [
      {
        label: 'Billable Hours',
        data: categoryBreakdown.map(cat => cat.billableHours),
        backgroundColor: '#4caf50',
        borderColor: '#4caf50',
        borderWidth: 1,
      },
      {
        label: 'Non-Billable Hours',
        data: categoryBreakdown.map(cat => cat.hours - cat.billableHours),
        backgroundColor: '#f44336',
        borderColor: '#f44336',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    layout: {
      padding: 10,
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    layout: {
      padding: 10,
    },
  };

  const uniqueCategories = Array.from(new Set(taskActivities.map(a => a.category)));

  if (error) {
    return (
      <Box sx={{ p: 2.5, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom>
          Work Activity Breakdown
        </Typography>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography color="error">Error: {error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 600, color: '#1a202c', mb: 4 }}
      >
        Work Activity Breakdown
      </Typography>

      {!taskActivities.length && !summary ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading work activity data...</Typography>
        </Paper>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Hours
                    </Typography>
                    <Typography variant="h4" component="div">
                      {summary.totalHours.toFixed(1)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Billability Rate
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ 
                      color: summary.billabilityRate >= 70 ? '#4caf50' : '#f44336' 
                    }}>
                      {summary.billabilityRate.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Tasks
                    </Typography>
                    <Typography variant="h4" component="div">
                      {summary.totalTasks}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Avg Task Size
                    </Typography>
                    <Typography variant="h4" component="div">
                      {summary.averageTaskSize.toFixed(1)}h
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Charts */}
          {categoryBreakdown.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={6}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', bgcolor: '#fff', width: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Hours by Category
                  </Typography>
                  <Box sx={{ height: 450, width: '100%', position: 'relative' }}>
                    <Pie data={categoryChartData} options={chartOptions} />
                  </Box>
                </Paper>
              </Grid>
              <Grid size={6}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', bgcolor: '#fff', width: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Billable vs Non-Billable by Category
                  </Typography>
                  <Box sx={{ height: 450, width: '100%', position: 'relative' }}>
                    <Bar data={billabilityChartData} options={barChartOptions} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', bgcolor: '#fff', mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Week</InputLabel>
                  <Select
                    value={selectedWeek}
                    label="Report Week"
                    onChange={(e) => setSelectedWeek(e.target.value)}
                  >
                    <MenuItem value="current">
                      {(() => {
                        const currentDate = new Date();
                        const dayOfWeek = currentDate.getDay();
                        const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                        const lastMonday = new Date(currentDate);
                        lastMonday.setDate(currentDate.getDate() - daysToLastMonday - 7);
                        const lastSunday = new Date(lastMonday);
                        lastSunday.setDate(lastMonday.getDate() + 6);
                        return `${lastMonday.toLocaleDateString()} - ${lastSunday.toLocaleDateString()}`;
                      })()}
                    </MenuItem>
                    {weeklyReports.map(report => (
                      <MenuItem key={report.weekOf} value={report.weekOf}>
                        Week of {new Date(report.weekOf).toLocaleDateString()} - {new Date(report.weekEnd).toLocaleDateString()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={3}>
                <TextField
                  fullWidth
                  label="Search tasks or projects"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {uniqueCategories.map(category => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Billability</InputLabel>
                  <Select
                    value={selectedBillability}
                    label="Billability"
                    onChange={(e) => setSelectedBillability(e.target.value)}
                  >
                    <MenuItem value="">All Tasks</MenuItem>
                    <MenuItem value="billable">Billable Only</MenuItem>
                    <MenuItem value="non-billable">Non-Billable Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    value={selectedTaskType}
                    label="Task Type"
                    onChange={(e) => setSelectedTaskType(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="recurring">Recurring</MenuItem>
                    <MenuItem value="one-off">One-off</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={2}>
                <TextField
                  fullWidth
                  label="Min Hours"
                  type="number"
                  value={minHours}
                  onChange={(e) => setMinHours(e.target.value)}
                  size="small"
                />
              </Grid>
              
            </Grid>
          </Paper>

          {/* Data Table */}
          <Paper elevation={3} sx={{ borderRadius: '12px', bgcolor: '#fff' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">
                Work Activities ({filteredActivities.length} of {taskActivities.length})
              </Typography>
            </Box>
            <DataTable
              columns={columns}
              data={filteredActivities}
              pagination
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              onChangeRowsPerPage={setRowsPerPage}
              customStyles={customStyles}
              responsive
              highlightOnHover
              striped
              defaultSortFieldId={4} // Sort by Total Hours
              defaultSortAsc={false}
            />
            <Box sx={{ p: 2, textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={exportToCSV}
                sx={{ borderRadius: '8px' }}
              >
                Export CSV
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default WorkActivityBreakdown;
